import { useState, type ReactElement } from 'react';
import { Accessibility, Palette } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';

import { getActiveTheme, setLocalThemeOverride } from './applyTheme';
import { THEMES, type ThemeId, type ThemeMeta } from './themes';

/**
 * "Theme" submenu for the user dropdown. Selecting a theme persists it to
 * localStorage (`ptv2:theme`) and applies it immediately.
 *
 * Layout (all driven by the theme registry in src/theme/themes.ts):
 *   • top, unlabeled — `category: 'official'` themes, plus an "Accessibility"
 *     sub-submenu holding the official themes flagged `accessibility: true`.
 *   • bottom, "Unofficial" — everything else, split by light / dark.
 *   • `hidden` themes are omitted entirely.
 */
const PICKER_THEMES = (THEMES as readonly ThemeMeta[]).filter(
  (theme) => theme.hidden !== true,
);
const OFFICIAL_THEMES = PICKER_THEMES.filter(
  (theme) => theme.category === 'official' && theme.accessibility !== true,
);
const ACCESSIBILITY_THEMES = PICKER_THEMES.filter(
  (theme) => theme.category === 'official' && theme.accessibility === true,
);
const UNOFFICIAL_THEMES = PICKER_THEMES.filter(
  (theme) => theme.category !== 'official',
);
const UNOFFICIAL_LIGHT = UNOFFICIAL_THEMES.filter((t) => t.mode === 'light');
const UNOFFICIAL_DARK = UNOFFICIAL_THEMES.filter((t) => t.mode === 'dark');

function themeRadioItem(theme: ThemeMeta): ReactElement {
  return (
    <DropdownMenuRadioItem key={theme.id} value={theme.id}>
      {theme.label}
    </DropdownMenuRadioItem>
  );
}

export function ThemeMenu(): ReactElement {
  const { t } = useTranslation();
  const [active, setActive] = useState<ThemeId>(() => getActiveTheme());

  const handleSelect = (value: string): void => {
    const next = value as ThemeId;
    setLocalThemeOverride(next); // writes localStorage + applies data-theme
    setActive(next);
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        <Palette className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <span>{t('shell.topNav.theme')}</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="min-w-44">
        <DropdownMenuRadioGroup value={active} onValueChange={handleSelect}>
          {/* Official (unlabeled) section + Accessibility sub-submenu. */}
          {OFFICIAL_THEMES.map(themeRadioItem)}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Accessibility
                className="h-4 w-4 text-muted-foreground"
                aria-hidden="true"
              />
              <span>{t('shell.topNav.themeAccessibility')}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-44">
              {ACCESSIBILITY_THEMES.length > 0 ? (
                ACCESSIBILITY_THEMES.map(themeRadioItem)
              ) : (
                <DropdownMenuItem disabled>
                  {t('shell.topNav.themeComingSoon')}
                </DropdownMenuItem>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          {/* Unofficial section — split by light / dark. */}
          <DropdownMenuSeparator />
          <DropdownMenuLabel className="text-muted-foreground">
            {t('shell.topNav.themeUnofficial')}
          </DropdownMenuLabel>
          {UNOFFICIAL_LIGHT.map(themeRadioItem)}
          {UNOFFICIAL_LIGHT.length > 0 && UNOFFICIAL_DARK.length > 0 && (
            <DropdownMenuSeparator />
          )}
          {UNOFFICIAL_DARK.map(themeRadioItem)}
        </DropdownMenuRadioGroup>
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
